import { useState } from 'react';
import { NJButton, NJHeading, NJText } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { PlanSegmentedControl } from './PlanSegmentedControl';
import {
  MapBlock, PlanEditorSteps, PlanEditorTabs, PlanList, Slot,
  type PlanStep, type StepMode, type WireframePlan,
} from './PlansWireframeParts';
import './configuration-wireframes.css';
import './plans-wireframes.css';

const VARIANTS = {
  A: {
    label: 'A · Panneau à deux niveaux',
    pitch: 'Un seul panneau latéral : la liste des plans, puis le plan choisi avec ses 3 étapes numérotées. La carte garde toute la largeur restante.',
    tradeoff: 'Le plus proche des panneaux du rail UrbanMap ; changer de plan demande un retour à la liste.',
  },
  B: {
    label: 'B · Liste + inspecteur',
    pitch: 'Liste des plans toujours visible à gauche, plan choisi dans un inspecteur à droite (onglets FDS Bâtiments / Tracé / Phasage), carte au centre.',
    tradeoff: 'Changer de plan en un clic ; la carte est plus étroite (deux panneaux ouverts).',
  },
  C: {
    label: 'C · Pleine page',
    pitch: 'Comme la configuration projet : page pleine largeur, liste des plans en colonne, onglets FDS en haut, carte au centre et bandeau de phasage en bas.',
    tradeoff: 'Le plus d’espace pour le phasage dans le temps ; on quitte la vue carte habituelle du projet.',
  },
} as const;
type Variant = keyof typeof VARIANTS;
const VARIANT_KEYS = Object.keys(VARIANTS) as Variant[];

const SEED: WireframePlan[] = [
  { id: 'p1', name: 'Scénario de référence' },
  { id: 'p2', name: 'Extension vers les écoles' },
  { id: 'p3', name: 'Boucle du centre-ville' },
  { id: 'p4', name: 'Raccordement des logements' },
];

/** Revue de layout uniquement : aucun contenu métier ni enregistrement. */
export function PlansWireframes() {
  const [variant, setVariant] = useState<Variant>('A');
  const [opened, setOpened] = useState(false);
  const [plans, setPlans] = useState(SEED);
  const [selected, setSelected] = useState<string | null>(null);
  const [step, setStep] = useState<PlanStep>('buildings');
  const [modes, setModes] = useState<Record<'route' | 'phasing', StepMode>>({ route: 'Manuel', phasing: 'Manuel' });

  const plan = plans.find((p) => p.id === selected) ?? null;
  const select = (id: string) => { setSelected(id); setStep('buildings'); };
  const create = () => {
    const id = `new-${plans.length + 1}`;
    setPlans((list) => [{ id, name: `Nouveau plan de développement ${plans.length - SEED.length + 1}` }, ...list]);
    select(id);
  };
  const editor = plan && { plan, step, onStep: setStep, modes, onMode: (s: 'route' | 'phasing', m: StepMode) => setModes((v) => ({ ...v, [s]: m })) };
  const list = (heading: boolean) => (
    <PlanList plans={plans} selected={selected} onSelect={select} onCreate={create} compact={variant !== 'A'} heading={heading} />
  );

  let layout: JSX.Element;
  if (variant === 'A') {
    layout = (
      <div className="pnv2-plans-layout">
        <aside className="pnv2-plans-panel" aria-label="Panneau Plans de développement">
          {editor ? <PlanEditorSteps {...editor} onBack={() => setSelected(null)} /> : list(true)}
        </aside>
        <MapBlock plan={plan} step={step} />
      </div>
    );
  } else if (variant === 'B') {
    layout = (
      <div className="pnv2-plans-layout">
        <aside className="pnv2-plans-panel pnv2-plans-panel--narrow" aria-label="Liste des plans">{list(true)}</aside>
        <MapBlock plan={plan} step={step} />
        <aside className="pnv2-plans-panel" aria-label="Plan choisi">
          {editor ? <PlanEditorTabs {...editor} /> : <Slot tall>Choisir ou créer un plan pour le définir</Slot>}
        </aside>
      </div>
    );
  } else {
    layout = (
      <section className="pnv2-plans-full" aria-label="Plans de développement pleine page">
        <header className="pnv2-review-heading">
          <NJHeading as="h2" scale="sm">Plans de développement</NJHeading>
          <NJButton label="Fermer" icon="close" variant="secondary" scale="sm" onClick={() => setOpened(false)} />
        </header>
        <div className="pnv2-plans-layout">
          <aside className="pnv2-plans-panel pnv2-plans-panel--narrow" aria-label="Liste des plans">{list(false)}</aside>
          <div className="pnv2-plans-main">
            {editor ? <PlanEditorTabs {...editor} /> : <Slot>Choisir ou créer un plan pour le définir</Slot>}
            <MapBlock plan={plan} step={step} />
            <Slot>Bandeau de phasage dans le temps (raccordements par période) — à détailler</Slot>
          </div>
        </div>
      </section>
    );
  }

  return (
    <PrototypeStudioLayout title="Plan navigation V3 - Revue">
      <div className="pnv2-review">
        <header className="pnv2-review-heading">
          <div>
            <NJHeading as="h1" scale="md">Plans de développement — revue de layout</NJHeading>
            <NJText scale="sm">Basse fidélité · organisation du panneau uniquement · contenu des étapes à détailler</NJText>
          </div>
          <div className="pnv2-plans-actions">
            <NJButton label="Revue configuration" href="?view=wireframes" variant="secondary" />
            <NJButton label="Retour au prototype" href="?view=prototype" variant="secondary" />
          </div>
        </header>
        <section className="pnv2-review-options" aria-label="Variantes de layout">
          <NJText as="strong">Hypothèse ouverte : organisation du panneau</NJText>
          <PlanSegmentedControl label="Variante de layout" options={VARIANT_KEYS.map((k) => VARIANTS[k].label)}
            value={VARIANTS[variant].label}
            onChange={(value) => setVariant(VARIANT_KEYS.find((k) => VARIANTS[k].label === value) ?? 'A')} />
          <NJText scale="sm">{VARIANTS[variant].pitch}</NJText>
          <NJText scale="sm">Compromis : {VARIANTS[variant].tradeoff}</NJText>
        </section>
        <div className="pnv2-review-steps" aria-label="Parcours proposé">
          <NJText>1. Ouvrir un projet</NJText>
          <NJText>2. Cliquer « Plans de développement » dans le rail</NJText>
          <NJText>3. Choisir ou créer un plan</NJText>
          <NJText>4. Définir bâtiments, tracé et phasage (manuel ou algorithme)</NJText>
        </div>
        <div className="pnv2-review-frame">
          <aside className="pnv2-review-rail" aria-label="Rail simplifié de la référence">
            <NJText as="strong">Plan</NJText>
            <NJText scale="sm">Accueil · Carte</NJText>
            <NJButton label="Plans de développement" icon="route" variant={opened ? 'primary' : 'secondary'}
              aria-pressed={opened} onClick={() => setOpened((v) => !v)} />
            <div className="pnv2-review-block"><NJText scale="sm">Autres entrées V3 inchangées</NJText></div>
          </aside>
          {opened ? layout : (
            <section className="pnv2-review-map" aria-label="Carte du projet">
              <div className="pnv2-review-block"><NJText>Projet ouvert — en-tête V3 inchangé</NJText></div>
              <NJHeading as="h2" scale="sm">Carte du projet</NJHeading>
              <NJText>Cliquer sur « Plans de développement » dans le rail.</NJText>
            </section>
          )}
        </div>
      </div>
    </PrototypeStudioLayout>
  );
}
